import { useEffect, useMemo, useRef, useState } from "react";
import {
  loadNaverMaps,
  JEJU_BOUNDS,
  JEJU_CENTER,
  ZOOM,
} from "../lib/naverMap";
import {
  buildClusterIndex,
  getClustersInView,
  getClusterExpansionZoom,
} from "../lib/clustering";
import {
  getRegionFeatures,
  computeBounds,
  geometryToNaverPaths,
} from "../lib/regions";
import { formatDuration } from "../lib/distance";

/**
 * 네이버 지도 + supercluster 거리 기반 클러스터링 + 맛집 마커 + 점선 그래프.
 *
 * 동작:
 * - cafes 가 들어오면 한 번만 supercluster 인덱스 빌드
 * - 지도 idle 이벤트마다 현재 viewport + 줌으로 query
 *   → 클러스터/개별 카페 마커 다시 그림
 * - hotspot (가까운 마커 많은 지역) 은 깊은 줌까지 묶임,
 *   한적한 지역은 일찍 풀려서 개별 마커 보임
 * - 카페 선택 모드: 클러스터링 우회. 모든 카페 직접 표시 + dim 처리 + 맛집/점선
 */
export default function MapView({
  cafes,
  places,
  selectedCafeId,
  selectedPlaceId,
  selectedCafe,
  userLocation,
  region,
  isDesktop,
  sheetMode,
  routeMode,
  onSelectCafe,
  onSelectPlace,
  onMapBlankClick,
  onMapReady,
}) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const cafeMarkersRef = useRef(new Map()); // cafeId or `cluster:${id}` → marker
  const placeMarkersRef = useRef({});
  const polylinesRef = useRef([]);
  const userMarkerRef = useRef(null);
  const regionPolygonsRef = useRef([]);
  const idleListenerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");

  const onMapBlankClickRef = useRef(onMapBlankClick);
  onMapBlankClickRef.current = onMapBlankClick;

  // 카페 데이터로 supercluster 인덱스 빌드 — cafes 가 바뀔 때만
  const clusterIndex = useMemo(() => buildClusterIndex(cafes), [cafes]);

  // 1) SDK 로드 + 지도 인스턴스
  useEffect(() => {
    let disposed = false;

    loadNaverMaps()
      .then((maps) => {
        if (disposed) return;

        const map = new maps.Map(mapEl.current, {
          center: new maps.LatLng(JEJU_CENTER.lat, JEJU_CENTER.lng),
          zoom: ZOOM.initial,
          minZoom: ZOOM.min,
          maxZoom: ZOOM.max,
          maxBounds: new maps.LatLngBounds(
            new maps.LatLng(JEJU_BOUNDS.south, JEJU_BOUNDS.west),
            new maps.LatLng(JEJU_BOUNDS.north, JEJU_BOUNDS.east)
          ),
          scaleControl: false,
          logoControl: true,
          logoControlOptions: { position: maps.Position.BOTTOM_LEFT },
          mapDataControl: false,
          zoomControl: false,
        });

        mapRef.current = map;
        setStatus("ready");
        onMapReady?.(map);

        maps.Event.addListener(map, "click", () => {
          onMapBlankClickRef.current?.();
        });
      })
      .catch((err) => {
        console.error(err);
        if (!disposed) {
          setErrorMsg(err.message);
          setStatus("error");
        }
      });

    return () => {
      disposed = true;
      cafeMarkersRef.current.forEach((m) => m.setMap(null));
      Object.values(placeMarkersRef.current).forEach((m) => m.setMap(null));
      polylinesRef.current.forEach((p) => p.setMap(null));
      regionPolygonsRef.current.forEach((p) => p.setMap(null));
      if (userMarkerRef.current) userMarkerRef.current.setMap(null);
      cafeMarkersRef.current.clear();
      placeMarkersRef.current = {};
      polylinesRef.current = [];
      regionPolygonsRef.current = [];
      userMarkerRef.current = null;
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) 카페 / 클러스터 마커 — viewport + 줌 기반으로 매번 다시 그림
  //    선택 모드일 땐 클러스터링 우회 (모든 카페 직접 표시)
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    const maps = window.naver.maps;
    const map = mapRef.current;

    const refresh = () => {
      // 기존 마커 정리
      cafeMarkersRef.current.forEach((m) => m.setMap(null));
      cafeMarkersRef.current.clear();

      // ── 카페 선택 모드: 클러스터링 우회 ─────────────────────
      if (selectedCafeId) {
        cafes.forEach((cafe) => {
          if (cafe.lat == null || cafe.lng == null) return;
          const isSelected = cafe.id === selectedCafeId;
          const isDim = !isSelected;
          const bubbleSize = isSelected ? 36 : 32;

          const marker = new maps.Marker({
            position: new maps.LatLng(cafe.lat, cafe.lng),
            map,
            title: cafe.name,
            icon: {
              content: buildCafeMarkerHtml({
                name: cafe.name,
                selected: isSelected,
                dim: isDim,
                cafeId: cafe.id,
              }),
              anchor: new maps.Point(60, bubbleSize + 7 + 3 + 22),
            },
            zIndex: isSelected ? 100 : 1,
          });
          maps.Event.addListener(marker, "click", () => onSelectCafe(cafe));
          cafeMarkersRef.current.set(cafe.id, marker);
        });
        return;
      }

      // ── 기본 모드: supercluster query ──────────────────────
      const bounds = map.getBounds();
      const sw = bounds.getSW();
      const ne = bounds.getNE();
      const bbox = {
        west: sw.lng(),
        south: sw.lat(),
        east: ne.lng(),
        north: ne.lat(),
      };
      const zoom = map.getZoom();
      const features = getClustersInView(clusterIndex, bbox, zoom);

      features.forEach((feature) => {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties;

        if (props.cluster) {
          // 클러스터 마커
          const count = props.point_count;
          const marker = new maps.Marker({
            position: new maps.LatLng(lat, lng),
            map,
            icon: buildClusterIcon({ count, maps }),
            zIndex: 20,
          });
          maps.Event.addListener(marker, "click", () => {
            const expZoom = getClusterExpansionZoom(clusterIndex, props.cluster_id);
            const targetZoom = expZoom != null
              ? Math.min(expZoom, ZOOM.max)
              : zoom + 2;
            map.morph(new maps.LatLng(lat, lng), targetZoom, { duration: 500 });
          });
          cafeMarkersRef.current.set(`cluster:${props.cluster_id}`, marker);
        } else {
          // 개별 카페 마커
          const cafe = props.cafe;
          const marker = new maps.Marker({
            position: new maps.LatLng(lat, lng),
            map,
            title: cafe.name,
            icon: {
              content: buildCafeMarkerHtml({ name: cafe.name, cafeId: cafe.id }),
              anchor: new maps.Point(60, 32 + 7 + 3 + 22),
            },
            zIndex: 10,
          });
          maps.Event.addListener(marker, "click", () => onSelectCafe(cafe));
          cafeMarkersRef.current.set(cafe.id, marker);
        }
      });
    };

    refresh();

    // 지도 이동/줌 끝나면 다시 query — 카페 선택 모드일 땐 그대로 유지
    if (idleListenerRef.current) {
      maps.Event.removeListener(idleListenerRef.current);
    }
    idleListenerRef.current = maps.Event.addListener(map, "idle", refresh);

    return () => {
      if (idleListenerRef.current) {
        maps.Event.removeListener(idleListenerRef.current);
        idleListenerRef.current = null;
      }
    };
  }, [cafes, clusterIndex, status, selectedCafeId, onSelectCafe]);

  // 3) 선택된 카페의 추천 맛집 마커 + 점선 그래프
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    const maps = window.naver.maps;
    const map = mapRef.current;

    Object.values(placeMarkersRef.current).forEach((m) => m.setMap(null));
    placeMarkersRef.current = {};
    polylinesRef.current.forEach((p) => p.setMap(null));
    polylinesRef.current = [];

    if (!selectedCafeId) return;

    const cafe = cafes.find((c) => c.id === selectedCafeId);
    if (!cafe) return;

    // 추천 명이 들어가 있는 풌 데이터는 selectedCafe (lazy load) 에서
    // 메타만 있는 단계에선 recommends 없음 → path/마커 안 그림
    const recommends = selectedCafe?.recommends;
    if (!recommends || recommends.length === 0) return;

    const cafeLatLng = new maps.LatLng(cafe.lat, cafe.lng);

    recommends.forEach((rec) => {
      const place = places.find((p) => p.id === rec.placeId);
      if (!place || place.lat == null || place.lng == null) return;

      const isSelected = place.id === selectedPlaceId;
      const placeLatLng = new maps.LatLng(place.lat, place.lng);

      // routeMode 에 따라 경로 선택: walking 기본, 데이터 없으면 driving fallback
      const routing = rec.routing;
      const drivingPathRaw = routing?.drivingPath;
      const walkingPathRaw = routing?.walking?.path;

      let pathRaw = null;
      let actualMode = null;  // 실제 그려진 경로의 모드
      if (routeMode === "walking" && walkingPathRaw?.length > 0) {
        pathRaw = walkingPathRaw;
        actualMode = "walking";
      } else if (drivingPathRaw?.length > 0) {
        pathRaw = drivingPathRaw;
        actualMode = "driving";
      }
      const hasPath = !!pathRaw;
      const path = hasPath
        ? pathRaw.map(([lng, lat]) => new maps.LatLng(lat, lng))
        : [cafeLatLng, placeLatLng];

      // 음식점 선택 시 선택 안 된 경로는 흐릿하게 (선택된 건 강조)
      const isDimmed = selectedPlaceId && !isSelected;
      const baseOpacity = hasPath ? 0.78 : 0.85;
      const lineOpacity = isDimmed ? 0.12 : baseOpacity;

      // 스타일: walking 은 점선, driving 은 실선
      // (path 없는 fallback 도 점선)
      const isDashed = actualMode === "walking" || !hasPath;

      const line = new maps.Polyline({
        map,
        path,
        strokeColor: "#D67522",
        strokeStyle: isDashed ? "shortdash" : "solid",
        strokeOpacity: lineOpacity,
        strokeWeight: hasPath ? 4 : 3,
        zIndex: isDimmed ? 30 : 40,
      });
      polylinesRef.current.push(line);

      // 경로 중간점에 시간 chip 마커 (routing 있을 때만)
      if (routing) {
        const midIdx = Math.floor(path.length / 2);
        const midPos = path[midIdx];
        // chip 시간: routeMode 에 따라 한 개만 표시 (N분 / N시간 N분)
        let chipText;
        if (routeMode === "walking") {
          const walkSec = routing.walking?.duration ?? routing.walkingDurationEst ?? 0;
          chipText = `🚶 ${formatDuration(walkSec)}`;
        } else {
          chipText = `🚗 ${formatDuration(routing.drivingDuration)}`;
        }
        // chip 사이즈에 따라 anchor x 동적으로 (대략 4.5px per char)
        const anchorX = Math.max(28, Math.round(chipText.length * 4.5));
        const timeMarker = new maps.Marker({
          position: midPos,
          map,
          icon: {
            content: buildRouteTimeMarkerHtml({ text: chipText, isSelected, isDimmed }),
            anchor: new maps.Point(anchorX, 12),
          },
          zIndex: isSelected ? 55 : isDimmed ? 35 : 50,
          clickable: false,
        });
        polylinesRef.current.push(timeMarker);
      }

      const marker = new maps.Marker({
        position: placeLatLng,
        map,
        title: place.name,
        icon: {
          content: buildPlaceMarkerHtml({
            name: place.name,
            selected: isSelected,
          }),
          anchor: new maps.Point(60, (isSelected ? 30 : 26) + 6 + 3 + 22),
        },
        // 카페 선택 모드에서는 맛집이 카페 마커보다 위 (걹치면 맛집이 보이도록)
        zIndex: isSelected ? 120 : 110,
      });

      maps.Event.addListener(marker, "click", () => onSelectPlace(place));
      placeMarkersRef.current[place.id] = marker;
    });
  }, [selectedCafeId, selectedCafe, selectedPlaceId, cafes, places, status, routeMode, onSelectPlace]);

  // 4) 선택된 카페 + 추천 맛집 마커 모두 한 화면에 — 자연스러운 fitBounds
  //    풌 데이터 로드 전엔 카페 centric morph, 로드 후엔 추천 포함 fitBounds
  const lastCafeIdRef = useRef(null);
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    if (!selectedCafeId) {
      lastCafeIdRef.current = null;
      return;
    }

    const cafe = cafes.find((c) => c.id === selectedCafeId);
    if (!cafe) return;

    const recommends = selectedCafe?.recommends;
    // (id, full|meta) 키로 두 단계 모두 고유하게
    const fitKey = `${selectedCafeId}:${recommends ? "full" : "meta"}`;
    if (lastCafeIdRef.current === fitKey) return;
    lastCafeIdRef.current = fitKey;

    const maps = window.naver.maps;
    const map = mapRef.current;

    // 풌 데이터 아직 안 로드 되었거나, 추천 맛집 좌표가 하나도 없으면 카페만 morph
    const recommendedCoords = (recommends || [])
      .map((rec) => places.find((p) => p.id === rec.placeId))
      .filter((p) => p && p.lat != null && p.lng != null);

    if (recommendedCoords.length === 0) {
      const currentZoom = map.getZoom();
      const targetZoom = Math.max(13, currentZoom);
      map.morph(
        new maps.LatLng(cafe.lat, cafe.lng),
        targetZoom,
        { duration: 600 }
      );
      return;
    }

    // 카페 + 맛집 좌표 모두 포함하는 bounds
    const bounds = new maps.LatLngBounds(
      new maps.LatLng(cafe.lat, cafe.lng),
      new maps.LatLng(cafe.lat, cafe.lng)
    );
    for (const p of recommendedCoords) {
      bounds.extend(new maps.LatLng(p.lat, p.lng));
    }

    // bounds 자체를 약간 확장 — 마커 분포에 비례하는 buffer 만 (최소값 강제 X)
    // 마커가 가까이 몰려있을 땐 buffer 도 작아져서 줌이 충분히 깊게 들어감.
    const sw = bounds.getSW();
    const ne = bounds.getNE();
    const latPad = (ne.lat() - sw.lat()) * 0.1;
    const lngPad = (ne.lng() - sw.lng()) * 0.1;
    const paddedBounds = new maps.LatLngBounds(
      new maps.LatLng(sw.lat() - latPad, sw.lng() - lngPad),
      new maps.LatLng(ne.lat() + latPad, ne.lng() + lngPad)
    );

    // viewport 가림 보정: 모바일은 시트 모드에 따라 동적, 데스크탑은 SidePanel 만큼
    const sheetReservePx = !isDesktop
      ? sheetMode === "full"
        ? window.innerHeight * 0.92
        : sheetMode === "half"
        ? window.innerHeight * 0.5
        : 280   // peek
      : 0;
    const padding = {
      top: isDesktop ? 60 : 110,
      bottom: isDesktop ? 30 : sheetReservePx + 40,
      left: isDesktop ? 450 : 30,
      right: 30,
    };

    map.fitBounds(paddedBounds, padding);
  }, [selectedCafeId, selectedCafe, cafes, places, status, isDesktop, sheetMode]);

  // 5) 맛집 선택 시 카메라 이동 — 시트 위 영역의 중앙으로
  const lastPlaceIdRef = useRef(null);
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    if (lastPlaceIdRef.current === selectedPlaceId) return;
    lastPlaceIdRef.current = selectedPlaceId;
    if (!selectedPlaceId) return;

    const place = places.find((p) => p.id === selectedPlaceId);
    if (!place || place.lat == null) return;
    const maps = window.naver.maps;
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    const targetZoom = Math.max(14, currentZoom);

    // 시트 보정: 마커를 viewport 정중앙이 아닌 "시트 위 영역의 중앙"으로
    // half 면 마커가 화면 위쪽 1/4 지점에 오게 — 즉 카메라 중심을 그 만큼 남쪽으로
    const sheetOffsetPx = !isDesktop
      ? sheetMode === "half"
        ? window.innerHeight * 0.25
        : sheetMode === "peek"
        ? 90
        : 0   // full: 어차피 지도 안 보임
      : 0;    // 데스크탑은 SidePanel 가림이 좌우라 morph 보정 불필요

    let targetCoord = new maps.LatLng(place.lat, place.lng);
    if (sheetOffsetPx > 0) {
      // 픽셀 → 위도(도) 변환 (Web Mercator, target zoom 기준)
      //   metersPerPixel = 156543.03392 * cos(lat) / 2^zoom
      //   1m ≈ 1/111320 도 (위도)
      const lat = place.lat;
      const metersPerPixel =
        (156543.03392 * Math.cos((lat * Math.PI) / 180)) /
        Math.pow(2, targetZoom);
      const offsetMeters = sheetOffsetPx * metersPerPixel;
      const offsetLat = offsetMeters / 111320;
      // 카메라를 마커보다 남쪽(작은 lat)으로 → 마커는 viewport 위쪽에 보임
      targetCoord = new maps.LatLng(lat - offsetLat, place.lng);
    }

    map.morph(targetCoord, targetZoom, { duration: 500 });
  }, [selectedPlaceId, places, status, isDesktop, sheetMode]);

  // 6) 사용자 현위치 마커 — 좌표 있을 때만 표시, 좌표 변경 시 setPosition으로 갱신
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    const maps = window.naver.maps;
    const map = mapRef.current;

    if (!userLocation || userLocation.lat == null) {
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
        userMarkerRef.current = null;
      }
      return;
    }

    const latLng = new maps.LatLng(userLocation.lat, userLocation.lng);

    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(latLng);
    } else {
      userMarkerRef.current = new maps.Marker({
        position: latLng,
        map,
        icon: {
          content: buildUserLocationMarkerHtml(),
          anchor: new maps.Point(20, 20),
        },
        zIndex: 200, // 카페/맛집/클러스터 마커보다 위
        clickable: false,
      });
    }
  }, [userLocation, status]);

  // 7) region polygon — 선택된 칩의 행정구역 경계를 지도 위에 그리고 fitBounds 로 카메라 맞춤
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    const maps = window.naver.maps;
    const map = mapRef.current;

    // 기존 polygon 제거
    regionPolygonsRef.current.forEach((p) => p.setMap(null));
    regionPolygonsRef.current = [];

    // "전체" 이면 지도를 제주 전체 view 로 되돌림
    if (!region || region === "all") {
      map.morph(
        new maps.LatLng(JEJU_CENTER.lat, JEJU_CENTER.lng),
        ZOOM.initial,
        { duration: 600 }
      );
      return;
    }

    const features = getRegionFeatures(region);
    if (features.length === 0) return;

    // 각 feature 의 polygon 그리기
    for (const f of features) {
      const polygonsPaths = geometryToNaverPaths(f.geometry, maps);
      for (const paths of polygonsPaths) {
        const polygon = new maps.Polygon({
          map,
          paths,
          fillColor: "#F08A3E",  // caramel-500 (warm orange)
          fillOpacity: 0.16,
          strokeColor: "#D67522",
          strokeWeight: 2,
          strokeOpacity: 0.7,
          strokeStyle: "solid",
          clickable: false,
          zIndex: 5,  // 카페/클러스터 마커보다 아래
        });
        regionPolygonsRef.current.push(polygon);
      }
    }

    // bounds 계산 후 fitBounds (viewport 가림 보정 포함)
    const b = computeBounds(features);
    const naverBounds = new maps.LatLngBounds(
      new maps.LatLng(b.minLat, b.minLng),
      new maps.LatLng(b.maxLat, b.maxLng)
    );

    // padding: 헤더 / SidePanel / BottomSheet 철하고
    const padding = {
      top: isDesktop ? 30 : 110,    // 모바일: 헤더(56) + 칩(44) + 여유
      bottom: 30,
      left: isDesktop ? 450 : 24,   // 데스크탑: SidePanel(420) + 여유
      right: 24,
    };

    map.fitBounds(naverBounds, padding);
  }, [region, status, isDesktop]);

  return (
    <div className="cf-map">
      <div ref={mapEl} className="cf-map__naver" />

      {status === "loading" && (
        <div className="cf-loading">
          <div className="cf-loading__text">지도 불러오는 중...</div>
        </div>
      )}

      {status === "error" && (
        <div className="cf-loading">
          <div className="cf-loading__error">
            <h3>지도를 불러오지 못했어요</h3>
            <p>{errorMsg}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── 카페 마커 (espresso bubble + ☕ or 특별 로고 + tail + label) ─────── */
function buildCafeMarkerHtml({ name, selected, dim, cafeId }) {
  const bubbleSize = selected ? 36 : 32;
  const bubbleBg = (cafeId === "cafe_001" || selected) ? "#1A1A1A" : "#3D2817";
  const bubbleScale = selected ? 1.06 : 1;
  const wrapperOpacity = dim ? 0.18 : 1;
  const wrapperScale = dim ? 0.84 : 1;

  // 커피파인더(cafe_001)는 자체 로고 PNG, 나머지는 ☕ 이모지
  const iconSize = bubbleSize - 16;
  const innerContent = cafeId === "cafe_001"
    ? `<img src="/coffee-finder-logo-small-white.png" alt="커피파인더" style="width: ${iconSize}px; height: ${iconSize}px; object-fit: contain; display: block;" />`
    : "☕";

  return `
    <div style="
      width: 120px;
      display: flex; flex-direction: column; align-items: center;
      cursor: pointer; pointer-events: auto;
      filter: drop-shadow(0 6px 14px rgba(42,26,14,0.30));
      opacity: ${wrapperOpacity};
      transform: scale(${wrapperScale});
      transition: opacity 240ms cubic-bezier(0.22,1,0.36,1),
                  transform 240ms cubic-bezier(0.22,1,0.36,1);
    ">
      <div style="
        width: ${bubbleSize}px; height: ${bubbleSize}px;
        border-radius: 999px;
        background: ${bubbleBg};
        display: flex; align-items: center; justify-content: center;
        color: #FAF5EC;
        font: 700 14px Pretendard, 'Apple SD Gothic Neo', sans-serif;
        border: 2.5px solid #FAF5EC;
        box-shadow: 0 4px 10px rgba(42,26,14,0.40);
        transform: scale(${bubbleScale});
      ">${innerContent}</div>
      <div style="
        width: 0; height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 7px solid #FAF5EC;
        margin-top: -2px;
      "></div>
      <div style="
        margin-top: 3px;
        font: 600 11px Pretendard, 'Apple SD Gothic Neo', sans-serif;
        color: #1A1A1A;
        background: rgba(250,245,236,0.94);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        padding: 3px 8px;
        border-radius: 999px;
        white-space: nowrap;
        box-shadow: 0 2px 6px rgba(42,26,14,0.15);
        letter-spacing: -0.01em;
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
      ">${name}</div>
    </div>
  `;
}

/* ─── 맛집 마커 ───────────────────────────────────────────── */
function buildPlaceMarkerHtml({ name, selected }) {
  const bubbleSize = selected ? 30 : 26;
  const bubbleBg = selected ? "#A07033" : "#C8995A";
  const dotSize = selected ? 10 : 8;
  const bubbleScale = selected ? 1.1 : 1;

  return `
    <div style="
      width: 120px;
      display: flex; flex-direction: column; align-items: center;
      cursor: pointer; pointer-events: auto;
      filter: drop-shadow(0 4px 10px rgba(42,26,14,0.30));
      transform: scale(${bubbleScale});
      transition: transform 220ms cubic-bezier(0.22,1,0.36,1);
    ">
      <div style="
        width: ${bubbleSize}px; height: ${bubbleSize}px;
        border-radius: 999px;
        background: ${bubbleBg};
        display: flex; align-items: center; justify-content: center;
        border: 2.5px solid #FAF5EC;
        box-shadow: 0 4px 10px rgba(42,26,14,0.30);
      ">
        <div style="
          width: ${dotSize}px; height: ${dotSize}px;
          border-radius: 999px;
          background: #3D2817;
        "></div>
      </div>
      <div style="
        width: 0; height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 6px solid #FAF5EC;
        margin-top: -2px;
      "></div>
      <div style="
        margin-top: 3px;
        font: 600 10.5px Pretendard, 'Apple SD Gothic Neo', sans-serif;
        color: #1A1A1A;
        background: rgba(250,245,236,0.94);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        padding: 2px 7px;
        border-radius: 999px;
        white-space: nowrap;
        box-shadow: 0 2px 6px rgba(42,26,14,0.15);
        letter-spacing: -0.01em;
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
      ">${name}</div>
    </div>
  `;
}

/* ─── 경로 중간 시간 chip 마커 (text 단일) ─── */
function buildRouteTimeMarkerHtml({ text, isSelected, isDimmed }) {
  const bg = isSelected ? "#D67522" : "#1A1A1A";
  const opacity = isDimmed ? 0.22 : isSelected ? 1 : 0.92;
  return `
    <div style="
      pointer-events: none;
      display: inline-flex; align-items: center; gap: 5px;
      padding: 4px 9px;
      background: ${bg};
      color: #FFFFFF;
      font: 600 11px Pretendard, 'Apple SD Gothic Neo', sans-serif;
      letter-spacing: -0.01em;
      border-radius: 999px;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.32);
      opacity: ${opacity};
      transition: background 240ms cubic-bezier(0.22,1,0.36,1),
                  opacity 240ms cubic-bezier(0.22,1,0.36,1);
    ">
      ${text}
    </div>
  `;
}

/* ─── 사용자 현위치 마커 (jeju-sea 점 + 펜스 애니메이션) ─── */
function buildUserLocationMarkerHtml() {
  return `
    <div style="
      position: relative;
      width: 40px; height: 40px;
      pointer-events: none;
    ">
      <div style="
        position: absolute; inset: 4px;
        border-radius: 999px;
        background: rgba(43,111,142,0.30);
        animation: cf-user-pulse 2s ease-out infinite;
      "></div>
      <div style="
        position: absolute; inset: 0;
        display: flex; align-items: center; justify-content: center;
      ">
        <div style="
          width: 16px; height: 16px;
          border-radius: 999px;
          background: #2B6F8E;
          border: 3px solid #FFFFFF;
          box-shadow: 0 2px 6px rgba(0,0,0,0.30);
        "></div>
      </div>
    </div>
  `;
}

/* ─── 클러스터 마커 (원 + 카운트 숫자만) ─── */
function buildClusterIcon({ count, maps }) {
  // 카운트에 따라 단계별 크기
  let size, fontSize;
  if (count < 5) { size = 40; fontSize = 14; }
  else if (count < 10) { size = 48; fontSize = 16; }
  else { size = 56; fontSize = 18; }

  return {
    content: `
      <div style="
        width: ${size}px; height: ${size}px;
        cursor: pointer; pointer-events: auto;
        filter: drop-shadow(0 8px 18px rgba(42,26,14,0.40));
      ">
        <div style="
          width: ${size}px; height: ${size}px;
          border-radius: 999px;
          background: #1A1A1A;
          display: flex; align-items: center; justify-content: center;
          color: #FAF5EC;
          font: 700 ${fontSize}px Pretendard, 'Apple SD Gothic Neo', sans-serif;
          border: 3px solid #FAF5EC;
          box-shadow: 0 6px 14px rgba(42,26,14,0.50);
          letter-spacing: -0.02em;
        ">${count}</div>
      </div>
    `,
    size: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
}
